import type { GlobalVariablesController } from '@divkitframework/divkit/typings/variables.d.ts';
import { creativeNumFromAdName, HOOK_IMAGE_VARIABLE, HOOK_TITLE_VARIABLE } from '@/config/hook';
import { getSafeSearchParams } from '@/config/searchParams';
import { focusCrop } from './focusCrop';

/**
 * Creative bank (PRD-4996): per creative number, a headline rewritten from the video's hook and the
 * poster UA set on the ad. Test snapshot lives in `public/hooks/creatives.json`; later the creative
 * pipeline publishes the same shape.
 */
export interface CreativeEntry {
  concept?: string;
  title?: Record<string, string>;
  image?: string;
}
export type CreativeBank = Record<string, CreativeEntry>;
export interface CreativeContent {
  title: string;
  image: string;
}

const EMPTY: CreativeContent = { title: '', image: '' };
export const CREATIVE_BANK_URL = '/hooks/creatives.json';
const IMAGE_TIMEOUT_MS = 1500;

export const pickCreativeContent = (
  bank: CreativeBank,
  adName: string | null | undefined,
  lang: string,
): CreativeContent => {
  const entry = bank[creativeNumFromAdName(adName)];
  if (!entry) return EMPTY;
  return { title: entry.title?.[lang] ?? '', image: entry.image ?? '' };
};

const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  new Promise((resolve) => {
    const timer = globalThis.setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => {
        globalThis.clearTimeout(timer);
        resolve(value);
      },
      () => {
        globalThis.clearTimeout(timer);
        resolve(fallback);
      },
    );
  });

export interface LoadOptions {
  adName: string | null | undefined;
  lang: string;
  fetchBank: () => Promise<CreativeBank>;
  /** Resolves to the url to show ('' when the image fails: Facebook poster links expire in days). */
  prepareImage: (url: string) => Promise<string>;
  timeoutMs?: number;
}

export const loadCreativeContent = async (options: LoadOptions): Promise<CreativeContent> => {
  if (!creativeNumFromAdName(options.adName)) return EMPTY;
  let bank: CreativeBank;
  try {
    bank = await options.fetchBank();
  } catch {
    return EMPTY;
  }
  const content = pickCreativeContent(bank, options.adName, options.lang);
  if (!content.image) return content;
  const image = await withTimeout(options.prepareImage(content.image), options.timeoutMs ?? IMAGE_TIMEOUT_MS, '');
  return { ...content, image };
};

/** Card the poster goes into (Loading, chair_2_0: 350×320) and where the crop keeps its focus. */
const CARD_ASPECT = 350 / 320;
const FOCUS_Y = 0.3;
const OUTPUT_WIDTH = 700;

const loadImage = (url: string, crossOrigin: boolean): Promise<InstanceType<typeof window.Image> | undefined> =>
  new Promise((resolve) => {
    const image = new window.Image();
    if (crossOrigin) image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image.naturalWidth > 0 ? image : undefined);
    image.onerror = () => resolve(undefined);
    image.src = url;
  });

/**
 * Posters are 9:16 with faces in the upper third; DivKit's `fill` crops around the centre. Cut the card
 * shape here with the focus at 30% and hand DivKit a ready blob. When the canvas is refused (no CORS),
 * the original poster is shown as is.
 */
const prepareImage = async (url: string): Promise<string> => {
  const image = await loadImage(url, true);
  if (!image) return (await loadImage(url, false)) ? url : '';
  try {
    const { sx, sy, sw, sh } = focusCrop(image.naturalWidth, image.naturalHeight, CARD_ASPECT, FOCUS_Y);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_WIDTH;
    canvas.height = Math.round(OUTPUT_WIDTH / CARD_ASPECT);
    canvas.getContext('2d')?.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Parameters<Parameters<typeof canvas.toBlob>[0]>[0]>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    return blob ? URL.createObjectURL(blob) : url;
  } catch {
    return url;
  }
};

let shared: Promise<CreativeContent> | undefined;
let settled: CreativeContent | undefined;

/**
 * One fetch per page load. `index.tsx` starts it at boot, in parallel with the quiz API and the layout,
 * so the bank and the poster are usually ready before the first screen renders.
 */
export const getCreativeContent = (): Promise<CreativeContent> => {
  shared ??= loadCreativeContent({
    adName: getSafeSearchParams().get('utm_ad_name'),
    lang: getSafeSearchParams().get('lang') || 'en',
    fetchBank: async () => {
      const response = await fetch(CREATIVE_BANK_URL);
      if (!response.ok) throw new Error(`creative bank HTTP ${response.status}`);
      return (await response.json()) as CreativeBank;
    },
    prepareImage,
  }).then((content) => (settled = content));
  return shared;
};

/** The loaded content, or undefined while it is still on its way. */
export const peekCreativeContent = (): CreativeContent | undefined => settled;

/**
 * Declares `hookTitle`/`hookImage`: with the loaded values when the boot preload already finished (no
 * fallback flash), otherwise empty and filled when it arrives.
 */
export const bindCreativeContent = (
  controller: GlobalVariablesController,
  ensure: (name: string, value: string) => void,
  load: () => Promise<CreativeContent> = getCreativeContent,
  peek: () => CreativeContent | undefined = peekCreativeContent,
): void => {
  const ready = peek();
  ensure(HOOK_TITLE_VARIABLE, ready?.title ?? '');
  ensure(HOOK_IMAGE_VARIABLE, ready?.image ?? '');
  if (ready) return;
  void load().then(({ title, image }) => {
    if (title) controller.getVariable(HOOK_TITLE_VARIABLE)?.set(title);
    if (image) controller.getVariable(HOOK_IMAGE_VARIABLE)?.set(image);
  });
};

/** Tests only. */
export const resetCreativeContent = (): void => {
  shared = undefined;
  settled = undefined;
};
